/**
 * Replaces emoji characters inside sanitized game-mode HTML with animated Lottie players.
 *
 * Game modes keep emitting plain emoji; this hook rewrites the rendered DOM after every
 * HTML update, so no game mode has to know about animations.
 */

import { createElement, useEffect, type RefObject } from 'react'
import { createRoot, type Root } from 'react-dom/client'

import { AnimatedEmoji } from '../components/low/AnimatedEmoji'
import {
  defaultPlayback,
  resolveAnimatedCodepoint,
  segmentGraphemes,
  type EmojiPlayback,
} from '../services/emojiAnimation'

/** Marks a span produced by this hook, so a re-run does not process it again. */
const PROCESSED_ATTRIBUTE = 'data-animated-emoji'

/** Per-occurrence playback override, set by a game mode on any ancestor within the region. */
const PLAYBACK_ATTRIBUTE = 'data-emoji-playback'

/** Opt-out for a whole subtree, set by a game mode. */
const DISABLED_ATTRIBUTE = 'data-emoji-animation'

/** The TTS caption element is rewritten every animation frame by useTtsCaptions. */
const TTS_CAPTIONS_ID = 'tts-captions'

/** A mounted animation together with the DOM it replaced, so cleanup can restore the text. */
interface MountedEmoji {
  root: Root
  host: HTMLSpanElement
  character: string
}

function isExcludedSubtree(element: Element): boolean {
  return (
    element.id === TTS_CAPTIONS_ID ||
    element.getAttribute(DISABLED_ATTRIBUTE) === 'off' ||
    element.hasAttribute(PROCESSED_ATTRIBUTE)
  )
}

/**
 * Finds the nearest playback override, searching no further than the region container.
 */
function readPlaybackOverride(node: Node, container: HTMLElement): EmojiPlayback | null {
  let current: Element | null =
    node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement
  while (current !== null) {
    const value = current.getAttribute(PLAYBACK_ATTRIBUTE)
    if (value === 'loop' || value === 'once') return value
    if (current === container) return null
    current = current.parentElement
  }
  return null
}

function collectTextNodes(container: HTMLElement): Text[] {
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => {
      const parent = node.parentElement
      if (parent === null) return NodeFilter.FILTER_REJECT
      for (let el: Element | null = parent; el !== null && el !== container; el = el.parentElement) {
        if (isExcludedSubtree(el)) return NodeFilter.FILTER_REJECT
      }
      return NodeFilter.FILTER_ACCEPT
    },
  })

  const nodes: Text[] = []
  let current = walker.nextNode()
  while (current !== null) {
    nodes.push(current as Text)
    current = walker.nextNode()
  }
  return nodes
}

/**
 * Rewrites one text node into a mix of plain text and animated emoji hosts.
 *
 * @returns the animations mounted for this node, empty when nothing was replaced.
 */
function replaceEmojiInTextNode(node: Text, container: HTMLElement): MountedEmoji[] {
  const graphemes = segmentGraphemes(node.data)
  if (!graphemes.some((grapheme) => resolveAnimatedCodepoint(grapheme) !== null)) {
    return []
  }

  const override = readPlaybackOverride(node, container)
  const fragment = document.createDocumentFragment()
  const mounted: MountedEmoji[] = []
  let buffer = ''

  const flushText = (): void => {
    if (buffer.length > 0) {
      fragment.appendChild(document.createTextNode(buffer))
      buffer = ''
    }
  }

  for (const grapheme of graphemes) {
    const codepoint = resolveAnimatedCodepoint(grapheme)
    if (codepoint === null) {
      buffer += grapheme
      continue
    }
    flushText()

    const playback = override ?? defaultPlayback(codepoint)
    const host = document.createElement('span')
    host.setAttribute(PROCESSED_ATTRIBUTE, codepoint)
    host.setAttribute(PLAYBACK_ATTRIBUTE, playback)
    fragment.appendChild(host)

    const root = createRoot(host)
    root.render(createElement(AnimatedEmoji, { codepoint, character: grapheme, playback }))
    mounted.push({ root, host, character: grapheme })
  }
  flushText()

  node.replaceWith(fragment)
  return mounted
}

/**
 * Animates every manifest-known emoji inside the referenced container.
 *
 * @param containerRef container whose innerHTML is managed by the caller
 * @param html current HTML, used as the change trigger; pass null to disable (empty region,
 *   or a region flagged unsafe, where game-mode scripts inspect the DOM themselves)
 */
export function useAnimatedEmoji(
  containerRef: RefObject<HTMLElement | null>,
  html: string | null
): void {
  useEffect(() => {
    const container = containerRef.current
    if (container === null || html === null || html === '') return

    // Mounted animations are tracked outside the DOM: dangerouslySetInnerHTML detaches the
    // host spans before cleanup runs, so they can no longer be found by traversing the tree.
    const mounted: MountedEmoji[] = []
    for (const node of collectTextNodes(container)) {
      mounted.push(...replaceEmojiInTextNode(node, container))
    }

    return () => {
      // When the HTML changes, React has already replaced the container's contents and the
      // hosts are detached — nothing to restore. The case that matters is a re-run over the
      // SAME DOM (StrictMode double-invoke): there the host is still attached and, left as
      // is, would stay empty forever, because the next pass skips it as already processed.
      for (const { host, character } of mounted) {
        if (host.isConnected) {
          const parent = host.parentNode
          host.replaceWith(document.createTextNode(character))
          parent?.normalize()
        }
      }
      // Deferred: React 18 warns when unmounting synchronously during a parent render.
      queueMicrotask(() => {
        for (const { root } of mounted) root.unmount()
      })
    }
  }, [containerRef, html])
}
